import type { SedrPhase, SedrPhasesList } from '../../api/sedr';
import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { formatPhaseDateRange } from './sedrUi';

export { formatPhaseDateRange } from './sedrUi';

export function phaseOptionHtml(
  phases: SedrPhase[],
  selectedPhaseId: string | null | undefined,
): string {
  const options = phases
    .map(
      (p) =>
        `<option value="${escapeAttr(p.id)}" ${p.id === selectedPhaseId ? 'selected' : ''}>${escapeHtml(p.name)}</option>`,
    )
    .join('');
  return `<option value="">${escapeHtml(t('sedr.phases.unassigned'))}</option>${options}`;
}

export function renderPhasesStrip(options: {
  list: SedrPhasesList;
  canWrite: boolean;
  onAdd?: () => void;
  onEdit?: (phase: SedrPhase) => void;
  onSelectPhase?: (phaseId: string | null) => void;
  /** When true, omit the built-in header (caller renders a matching section header). */
  omitHeader?: boolean;
}): HTMLElement {
  const { list, canWrite } = options;
  const section = document.createElement('section');
  section.className = 'sedr-phases';
  section.innerHTML = `
    ${options.omitHeader
      ? ''
      : `<div class="sedr-phases__header">
      <h3 data-i18n="sedr.phases.title"></h3>
      ${canWrite ? `<button type="button" class="button subtle" id="sedr-phase-add" data-i18n="sedr.phases.add"></button>` : ''}
    </div>`}
    <div class="sedr-phases__strip" id="sedr-phases-strip"></div>
  `;

  const strip = section.querySelector('#sedr-phases-strip') as HTMLElement;
  if (list.phases.length === 0 && list.unassignedRequirementCount === 0) {
    strip.innerHTML = `<p class="muted" data-i18n="sedr.phases.empty"></p>`;
  } else {
    for (const phase of list.phases) {
      strip.append(renderPhaseCard(phase, canWrite, options.onEdit, options.onSelectPhase));
    }
    if (list.unassignedRequirementCount > 0) {
      const unassigned = document.createElement('div');
      unassigned.className = `sedr-phase-card sedr-phase-card--unassigned${
        options.onSelectPhase ? ' sedr-phase-card--clickable' : ''
      }`;
      unassigned.innerHTML = `
        <div class="sedr-phase-card__name" data-i18n="sedr.phases.unassigned"></div>
        <div class="sedr-phase-card__meta muted">${t('sedr.phases.assignedCount', {
          count: list.unassignedRequirementCount,
        })}</div>
      `;
      if (options.onSelectPhase) {
        unassigned.tabIndex = 0;
        unassigned.setAttribute('role', 'button');
        const activate = () => options.onSelectPhase?.(null);
        unassigned.addEventListener('click', activate);
        unassigned.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        });
      }
      strip.append(unassigned);
    }
  }

  applyI18n(section);
  if (!options.omitHeader) {
    section.querySelector('#sedr-phase-add')?.addEventListener('click', () => options.onAdd?.());
  }
  return section;
}

function renderPhaseCard(
  phase: SedrPhase,
  canWrite: boolean,
  onEdit?: (phase: SedrPhase) => void,
  onSelectPhase?: (phaseId: string | null) => void,
): HTMLElement {
  const card = document.createElement('div');
  const { progress } = phase;
  const isComplete = progress.totalAssigned > 0 && progress.completionPercent >= 100;
  const isArchived = Boolean(phase.archivedAt);
  card.className = [
    'sedr-phase-card',
    onSelectPhase && !isArchived ? 'sedr-phase-card--clickable' : '',
    isComplete ? 'sedr-phase-card--complete' : '',
    isArchived ? 'sedr-phase-card--archived' : '',
  ]
    .filter(Boolean)
    .join(' ');
  card.innerHTML = `
    <div class="sedr-phase-card__top">
      <div class="sedr-phase-card__name">${escapeHtml(phase.name)}${
        isArchived ? ` <span class="muted">(${escapeHtml(t('sedr.phases.archived'))})</span>` : ''
      }</div>
      ${canWrite ? `<button type="button" class="button ghost sedr-phase-card__edit" data-i18n="sedr.phases.edit"></button>` : ''}
    </div>
    <div class="sedr-phase-card__meta muted">${escapeHtml(formatPhaseDateRange(phase))}</div>
    <div class="sedr-phase-card__progress" role="progressbar" aria-valuenow="${progress.completionPercent}" aria-valuemin="0" aria-valuemax="100">
      <div class="sedr-phase-card__progress-bar" style="width: ${progress.completionPercent}%"></div>
    </div>
    <div class="sedr-phase-card__counts muted">
      ${t('sedr.phases.progressSummary', {
        approved: progress.approved,
        total: progress.totalAssigned,
        percent: progress.completionPercent,
      })}
    </div>
  `;
  applyI18n(card);
  card.querySelector('.sedr-phase-card__edit')?.addEventListener('click', (e) => {
    e.stopPropagation();
    onEdit?.(phase);
  });
  if (onSelectPhase && !isArchived) {
    card.tabIndex = 0;
    card.setAttribute('role', 'button');
    const activate = () => onSelectPhase(phase.id);
    card.addEventListener('click', activate);
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    });
  }
  return card;
}

export function showPhaseDialog(options: {
  roomId: string;
  phase?: SedrPhase | null;
  onSaved: () => Promise<void> | void;
}): void {
  const editing = options.phase ?? null;
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog';
  dialog.innerHTML = `
    <h2 data-i18n="${editing ? 'sedr.phases.editTitle' : 'sedr.phases.addTitle'}"></h2>
    <form class="form-grid" id="sedr-phase-form">
      <label>
        <span data-i18n="sedr.phases.name"></span>
        <input name="name" required maxlength="200" value="${escapeAttr(editing?.name ?? '')}" />
      </label>
      <label>
        <span data-i18n="sedr.phases.startAt"></span>
        <input type="date" name="startAt" value="${toDateInput(editing?.startAt)}" />
      </label>
      <label>
        <span data-i18n="sedr.phases.endAt"></span>
        <input type="date" name="endAt" value="${toDateInput(editing?.endAt)}" />
      </label>
      <label>
        <span data-i18n="sedr.phases.sortOrder"></span>
        <input type="number" name="sortOrder" min="0" step="1" value="${editing?.sortOrder ?? ''}" />
      </label>
      <p class="error-text" id="sedr-phase-error" hidden></p>
      <div class="modal-dialog__actions">
        <button type="button" class="button ghost" id="sedr-phase-cancel" data-i18n="common.cancel"></button>
        ${
          editing
            ? editing.archivedAt
              ? `<button type="button" class="button ghost" id="sedr-phase-unarchive" data-i18n="sedr.phases.unarchive"></button>`
              : `<button type="button" class="button ghost" id="sedr-phase-archive" data-i18n="sedr.phases.archive"></button>`
            : ''
        }
        <button type="submit" class="button primary" data-i18n="sedr.phases.save"></button>
      </div>
    </form>
  `;
  backdrop.append(dialog);
  document.body.append(backdrop);
  applyI18n(dialog);

  const close = () => backdrop.remove();
  dialog.querySelector('#sedr-phase-cancel')?.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  dialog.querySelector('#sedr-phase-archive')?.addEventListener('click', () => {
    void (async () => {
      if (!editing) return;
      const errorEl = dialog.querySelector('#sedr-phase-error') as HTMLParagraphElement;
      try {
        await sedrApi.archivePhase(options.roomId, editing.id);
        close();
        await options.onSaved();
      } catch {
        errorEl.hidden = false;
        errorEl.textContent = t('status.error');
      }
    })();
  });

  dialog.querySelector('#sedr-phase-unarchive')?.addEventListener('click', () => {
    void (async () => {
      if (!editing) return;
      const errorEl = dialog.querySelector('#sedr-phase-error') as HTMLParagraphElement;
      try {
        await sedrApi.unarchivePhase(options.roomId, editing.id);
        close();
        await options.onSaved();
      } catch {
        errorEl.hidden = false;
        errorEl.textContent = t('status.error');
      }
    })();
  });

  dialog.querySelector('#sedr-phase-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    void (async () => {
      const form = e.target as HTMLFormElement;
      const data = new FormData(form);
      const errorEl = dialog.querySelector('#sedr-phase-error') as HTMLParagraphElement;
      const name = String(data.get('name') ?? '').trim();
      const startRaw = String(data.get('startAt') ?? '').trim();
      const endRaw = String(data.get('endAt') ?? '').trim();
      const sortRaw = String(data.get('sortOrder') ?? '').trim();
      const startAt = startRaw ? new Date(`${startRaw}T00:00:00.000Z`).toISOString() : null;
      const endAt = endRaw ? new Date(`${endRaw}T00:00:00.000Z`).toISOString() : null;
      const sortOrder = sortRaw === '' ? null : Number(sortRaw);

      try {
        if (editing) {
          await sedrApi.patchPhase(options.roomId, editing.id, {
            name,
            startAt: startAt ?? undefined,
            endAt: endAt ?? undefined,
            clearStartAt: !startAt,
            clearEndAt: !endAt,
            sortOrder,
          });
        } else {
          await sedrApi.createPhase(options.roomId, {
            name,
            startAt,
            endAt,
            sortOrder,
          });
        }
        close();
        await options.onSaved();
      } catch {
        errorEl.hidden = false;
        errorEl.textContent = t('status.error');
      }
    })();
  });
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, '&#39;');
}
